/**
 * Полоска уровня — доказательство под карточкой ответа (UX_SPEC §6.3).
 *
 * Ровно одно измерение: NDVI на размеченной оси, — и то же самое названо СЛОВАМИ рядом,
 * с ДАТОЙ СНИМКА. Дата обязательна: «Сейчас на поле: открытая почва» обещало сегодняшний
 * день по снимку, которому бывает три недели облаков, а между «было вчера» и «было 40 дней
 * назад» лежит разница между «не езжай» и «мы не знаем» (§3.3). Без даты полоска не
 * рисуется вовсе — недатированное утверждение о поле продукт не делает.
 *
 * Ни одной новой границы: зоны нарезаны теми же `DISPLAY_UNCERTAIN_NDVI_LOW/HIGH`, класс
 * читается тем же `toneOf`, что и колонка полосы, а высота оси — тот же
 * `DISPLAY_NDVI_AXIS_MAX`. Полоска НЕ повторяет полосу состояния: у неё нет оси времени,
 * нет отклонённых дат и нет скобки события — это уровень, а не история.
 *
 * Полый маркер — локальный максимум за 60 дней. Число пика печатается только «при числах»:
 * без него фраза остаётся предложением, а не обрубком с повисшим тире.
 */

import { T } from "../strings";
import {
  DISPLAY_NDVI_AXIS_MAX,
  DISPLAY_UNCERTAIN_NDVI_HIGH,
  DISPLAY_UNCERTAIN_NDVI_LOW,
} from "../lib/constants";
import { formatDayMonth, num } from "../lib/format";
import { toneOf, type StripTone } from "../lib/strip";

const TONE_LABEL: Record<StripTone, string> = {
  vegetation: T.strip.bands.vegetation,
  stubble: T.strip.bands.stubble,
  bare: T.strip.bands.bare,
  unknown: T.common.noData,
};

interface Props {
  /** NDVI сейчас. `null` — полоска не рисуется вовсе: причину печатает блок вокруг. */
  ndviNow: number | null;
  /** NDVI локального максимума за 60 дней, если он есть. */
  peakNdvi: number | null;
  /** Дата снимка, с которого снято `ndviNow`. Без неё утверждения не будет. */
  observationDate: string | null;
  /** Дата локального максимума. Без неё строка про пик не печатается. */
  peakDate: string | null;
  /** «Числа»: только они добавляют к фразе про пик само число NDVI. */
  showNumbers?: boolean;
}

const percentOf = (ndvi: number): number =>
  Math.min(Math.max(ndvi, 0) / DISPLAY_NDVI_AXIS_MAX, 1) * 100;

export function LevelBar({
  ndviNow,
  peakNdvi,
  observationDate,
  peakDate,
  showNumbers = false,
}: Props) {
  if (ndviNow === null || observationDate === null) return null;

  const tone = toneOf(ndviNow);
  const nowPercent = percentOf(ndviNow);
  const barePercent = percentOf(DISPLAY_UNCERTAIN_NDVI_LOW);
  const stubblePercent = percentOf(DISPLAY_UNCERTAIN_NDVI_HIGH);
  const peakPercent = peakNdvi === null ? null : percentOf(peakNdvi);
  const observation = T.vegetation.observation(formatDayMonth(observationDate), TONE_LABEL[tone]);
  const peakNote =
    peakDate === null
      ? null
      : T.vegetation.peakNote(
          showNumbers && peakNdvi !== null ? num(peakNdvi, 2) : null,
          formatDayMonth(peakDate),
        );

  return (
    <div className="k-level" data-testid="level-bar">
      <div className="k-level__track" role="img" aria-label={observation}>
        <span
          className="k-level__zone k-level__zone--bare"
          style={{ left: 0, width: `${barePercent}%` }}
        />
        <span
          className="k-level__zone k-level__zone--stubble"
          style={{ left: `${barePercent}%`, width: `${stubblePercent - barePercent}%` }}
        />
        <span
          className="k-level__zone k-level__zone--veg"
          style={{ left: `${stubblePercent}%`, width: `${100 - stubblePercent}%` }}
        />

        {/* Максимум рисуется ПОД текущим уровнем в порядке DOM: когда они совпали, видно
            сплошной маркер, а не призрак поверх него. */}
        {peakPercent !== null && (
          <span
            className="k-level__mark k-level__mark--peak"
            data-testid="level-peak"
            data-percent={peakPercent.toFixed(1)}
            style={{ left: `${peakPercent}%` }}
          />
        )}
        <span
          className="k-level__mark k-level__mark--now"
          data-testid="level-now"
          data-tone={tone}
          data-percent={nowPercent.toFixed(1)}
          style={{ left: `${nowPercent}%` }}
        />
      </div>

      <div className="k-level__zones" aria-hidden="true">
        <span style={{ width: `${barePercent}%` }}>{T.strip.bands.bare}</span>
        <span style={{ width: `${stubblePercent - barePercent}%` }}>{T.strip.bands.stubble}</span>
        <span style={{ width: `${100 - stubblePercent}%` }}>{T.strip.bands.vegetation}</span>
      </div>

      <p className="k-caption k-level__reading" data-testid="level-reading">
        {observation}
      </p>
      {peakNote && (
        <p className="k-caption k-level__peaknote" data-testid="level-peaknote">
          {peakNote}
        </p>
      )}
    </div>
  );
}
